import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { VendorProvider } from './contexts/VendorContext';
import { Toaster } from './components/ui/sonner';
import AdminDomainGuard from './components/AdminDomainGuard';
import CustomerLayout from './components/CustomerLayout';
import AnalyticsTracker from './components/AnalyticsTracker';

// Import pages
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import TrackOrderPage from './pages/TrackOrderPage';

// SEO Landing Pages
import GaneshBakeryMacaroonPage from './pages/GaneshBakeryMacaroonPage';
import TirunelveliHalwaPage from './pages/TirunelveliHalwaPage';
import SatturSevuPage from './pages/SatturSevuPage';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminIntegrations from './pages/admin/AdminIntegrations';
import AdminPackages from './pages/admin/AdminPackages';
import AdminPackageBookings from './pages/admin/AdminPackageBookings';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminPageAnalytics from './pages/admin/AdminPageAnalytics';
import AdminBusinessHealth from './pages/admin/AdminBusinessHealth';
import AdminCategories from './pages/admin/AdminCategories';
import AdminContentSettings from './pages/admin/AdminContentSettings';
import AdminPaymentConfig from './pages/admin/AdminPaymentConfig';
import AdminCODSettings from './pages/admin/AdminCODSettings';
import AdminAbandonedCarts from './pages/admin/AdminAbandonedCarts';
import AdminSEOSettings from './pages/admin/AdminSEOSettings';
import AdminBroaderRegions from './pages/admin/AdminBroaderRegions';
import AdminShippingZones from './pages/admin/AdminShippingZones';
import AdminTrending from './pages/admin/AdminTrending';
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorRegisterPage from './pages/vendor/VendorRegisterPage';
import VendorLoginPage from './pages/vendor/VendorLoginPage';
import VendorDashboardNew from './pages/vendor/VendorDashboardNew';
import VendorDashboardEnhanced from './pages/vendor/VendorDashboardEnhanced';
import VendorProducts from './pages/vendor/VendorProducts';
import VendorProductForm from './pages/vendor/VendorProductForm';
import VendorOrders from './pages/vendor/VendorOrders';
import VendorPayments from './pages/vendor/VendorPayments';
import VendorSettings from './pages/vendor/VendorSettings';
import VendorAnalytics from './pages/vendor/VendorAnalytics';
import VendorReturns from './pages/vendor/VendorReturns';
import VendorStoreSettings from './pages/vendor/VendorStoreSettings';
import VendorBulkOperations from './pages/vendor/VendorBulkOperations';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminVendors from './pages/admin/AdminVendors';
import AdminVendorAnalytics from './pages/admin/AdminVendorAnalytics';
import AdminProductApprovals from './pages/admin/AdminProductApprovals';
import AdminBanners from './pages/admin/AdminBanners';
import AdminContent from './pages/admin/AdminContent';
import AdminProductHighlights from './pages/admin/AdminProductHighlights';
import AdminBulkOperations from './pages/admin/AdminBulkOperations';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import NammaYatraPage from './pages/NammaYatraPage';
import PackageBookingPage from './pages/PackageBookingPage';
import PackageDetailPage from './pages/PackageDetailPage';
import RegionalMapPage from './pages/RegionalMapPage';
import ContentPage from './pages/ContentPage';
import InternationalPage from './pages/InternationalPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import AboutPage from './pages/AboutPage';
import CollectionsPage from './pages/CollectionsPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import VendorStorePage from './pages/VendorStorePage';

// POS Pages
import POSLogin from './pages/pos/POSLogin';
import POSBilling from './pages/pos/POSBilling';
import POSAdminLogin from './pages/pos/POSAdminLogin';
import POSAdminDashboard from './pages/pos/POSAdminDashboard';
import AIProductOnboard from './pages/pos/AIProductOnboard';
import POSAdminProducts from './pages/pos/POSAdminProducts';
import POSAdminPurchaseOrders from './pages/pos/POSAdminPurchaseOrders';
import POSAdminSuppliers from './pages/pos/POSAdminSuppliers';
import POSStoreManagerGRN from './pages/pos/POSStoreManagerGRN';
import POSAdminLayout from './components/POSAdminLayout';
import AdminPOSDashboard from './pages/admin/AdminPOSDashboard';
import AdminPOSManagers from './pages/admin/AdminPOSManagers';
import AdminPOSSettings from './pages/admin/AdminPOSSettings';
import AdminPOSOrders from './pages/admin/AdminPOSOrders';
import AdminPOSDiscounts from './pages/admin/AdminPOSDiscounts';
import AdminPOSReports from './pages/admin/AdminPOSReports';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import VendorProtectedRoute from './components/VendorProtectedRoute';
import AdminLayout from './components/AdminLayout';
import ScrollToTop from './components/ScrollToTop';

import './App.css';

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <VendorProvider>
            <Router>
              <ScrollToTop />
              <AnalyticsTracker />
              <Routes>
                {/* SEO Landing Pages */}
                <Route path="/ganesh-bakery-macaroon" element={
                  <CustomerLayout><GaneshBakeryMacaroonPage /></CustomerLayout>
                } />
                <Route path="/tirunelveli-halwa" element={
                  <CustomerLayout><TirunelveliHalwaPage /></CustomerLayout>
                } />
                <Route path="/sattur-sevu" element={
                  <CustomerLayout><SatturSevuPage /></CustomerLayout>
                } />
                
                {/* Admin Login - Only accessible from admin subdomain */}
                <Route path="/admin/login" element={
                  <AdminDomainGuard>
                    <AdminLoginPage />
                  </AdminDomainGuard>
                } />

              {/* Admin Routes - Only accessible from admin subdomain */}
              <Route path="/admin/*" element={
                <AdminDomainGuard>
                  <ProtectedRoute role="admin">
                    <AdminLayout>
                      <Routes>
                        <Route index element={<AdminDashboard />} />
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="vendors" element={<AdminVendors />} />
                        <Route path="vendor-analytics" element={<AdminVendorAnalytics />} />
                        <Route path="product-approvals" element={<AdminProductApprovals />} />
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="orders" element={<AdminOrders />} />
                        <Route path="customers" element={<AdminCustomers />} />
                        <Route path="packages" element={<AdminPackages />} />
                        <Route path="package-bookings" element={<AdminPackageBookings />} />
                        <Route path="content-settings" element={<AdminContentSettings />} />
                        <Route path="analytics" element={<AdminAnalytics />} />
                        <Route path="page-analytics" element={<AdminPageAnalytics />} />
                        <Route path="business-health" element={<AdminBusinessHealth />} />
                        <Route path="discounts" element={<AdminDiscounts />} />
                        <Route path="integrations" element={<AdminIntegrations />} />
                        <Route path="payment-config" element={<AdminPaymentConfig />} />
                        <Route path="broader-regions" element={<AdminBroaderRegions />} />
                        <Route path="shipping-zones" element={<AdminShippingZones />} />
                        <Route path="cod-settings" element={<AdminCODSettings />} />
                        <Route path="trending" element={<AdminTrending />} />
                        <Route path="banners" element={<AdminBanners />} />
                        <Route path="content" element={<AdminContent />} />
                        <Route path="product-highlights" element={<AdminProductHighlights />} />
                        <Route path="bulk-operations" element={<AdminBulkOperations />} />
                        <Route path="abandoned-carts" element={<AdminAbandonedCarts />} />
                        <Route path="seo-settings" element={<AdminSEOSettings />} />
                        {/* POS Admin Routes */}
                        <Route path="pos" element={<AdminPOSDashboard />} />
                        <Route path="pos/dashboard" element={<AdminPOSDashboard />} />
                        <Route path="pos/managers" element={<AdminPOSManagers />} />
                        <Route path="pos/settings" element={<AdminPOSSettings />} />
                        <Route path="pos/orders" element={<AdminPOSOrders />} />
                        <Route path="pos/discounts" element={<AdminPOSDiscounts />} />
                        <Route path="pos/reports" element={<AdminPOSReports />} />
                      </Routes>
                    </AdminLayout>
                  </ProtectedRoute>
                </AdminDomainGuard>
              } />

              {/* POS Routes - Separate from Admin, accessible at /pos */}
              <Route path="/pos/login" element={<POSLogin />} />
              <Route path="/pos" element={<POSBilling />} />
              <Route path="/pos/grn" element={<POSStoreManagerGRN />} />

              {/* POS Admin Routes - Dedicated POS Admin interface (different from main /admin) */}
              <Route path="/pos/admin/login" element={<POSAdminLogin />} />
              <Route path="/pos/admin" element={<POSAdminLayout><POSAdminDashboard /></POSAdminLayout>} />
              <Route path="/pos/admin/dashboard" element={<POSAdminLayout><POSAdminDashboard /></POSAdminLayout>} />
              <Route path="/pos/admin/ai-onboard" element={<POSAdminLayout><AIProductOnboard /></POSAdminLayout>} />
              <Route path="/pos/admin/products" element={<POSAdminLayout><POSAdminProducts /></POSAdminLayout>} />
              <Route path="/pos/admin/purchase-orders" element={<POSAdminLayout><POSAdminPurchaseOrders /></POSAdminLayout>} />
              <Route path="/pos/admin/suppliers" element={<POSAdminLayout><POSAdminSuppliers /></POSAdminLayout>} />
              <Route path="/pos/admin/inventory" element={<POSAdminLayout><POSAdminProducts /></POSAdminLayout>} />
              <Route path="/pos/admin/sales" element={<POSAdminLayout><AdminPOSOrders /></POSAdminLayout>} />
              <Route path="/pos/admin/managers" element={<POSAdminLayout><AdminPOSManagers /></POSAdminLayout>} />
              <Route path="/pos/admin/discounts" element={<POSAdminLayout><AdminPOSDiscounts /></POSAdminLayout>} />
              <Route path="/pos/admin/reports" element={<POSAdminLayout><AdminPOSReports /></POSAdminLayout>} />
              <Route path="/pos/admin/settings" element={<POSAdminLayout><AdminPOSSettings /></POSAdminLayout>} />

              {/* Vendor Routes - No Navbar/Footer */}
              <Route path="/vendor/register" element={<VendorRegisterPage />} />
              <Route path="/vendor/login" element={<VendorLoginPage />} />
              <Route path="/vendor/dashboard" element={
                <VendorProtectedRoute>
                  <VendorDashboardEnhanced />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/products" element={
                <VendorProtectedRoute>
                  <VendorProducts />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/products/new" element={
                <VendorProtectedRoute>
                  <VendorProductForm />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/products/:id/edit" element={
                <VendorProtectedRoute>
                  <VendorProductForm />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/orders" element={
                <VendorProtectedRoute>
                  <VendorOrders />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/returns" element={
                <VendorProtectedRoute>
                  <VendorReturns />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/payments" element={
                <VendorProtectedRoute>
                  <VendorPayments />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/inventory" element={
                <VendorProtectedRoute>
                  <VendorProducts />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/analytics" element={
                <VendorProtectedRoute>
                  <VendorAnalytics />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/settings" element={
                <VendorProtectedRoute>
                  <VendorSettings />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/store-settings" element={
                <VendorProtectedRoute>
                  <VendorStoreSettings />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/bulk-operations" element={
                <VendorProtectedRoute>
                  <VendorBulkOperations />
                </VendorProtectedRoute>
              } />
              <Route path="/vendor/*" element={
                <VendorProtectedRoute>
                  <VendorDashboardEnhanced />
                </VendorProtectedRoute>
              } />

              {/* Main Homepage - Uses CustomerLayout */}
              <Route path="/" element={<HomePage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/marketplace" element={<HomePage />} />

              {/* Static Content Pages - Dynamic CMS */}
              <Route path="/about" element={<ContentPage />} />
              <Route path="/about-us" element={<ContentPage />} />
              <Route path="/contact" element={<ContentPage />} />
              <Route path="/help" element={<ContentPage />} />
              <Route path="/help-faq" element={<ContentPage />} />
              <Route path="/privacy" element={<ContentPage />} />
              <Route path="/privacy-policy" element={<ContentPage />} />
              <Route path="/terms" element={<ContentPage />} />
              <Route path="/terms-of-service" element={<ContentPage />} />
              <Route path="/refund" element={<ContentPage />} />
              <Route path="/refund-policy" element={<ContentPage />} />
              <Route path="/page/:slug" element={<ContentPage />} />

              {/* All Customer Routes - Use CustomerLayout directly in each page */}
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/vendor/:vendorId/store" element={<VendorStorePage />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/international" element={<InternationalPage />} />
              <Route path="/nammayatra" element={<NammaYatraPage />} />
              <Route path="/regional-map" element={<RegionalMapPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/cart" element={<CustomerLayout><CartPage /></CustomerLayout>} />
              <Route path="/track-order" element={<TrackOrderPage />} />
              <Route path="/package/:id" element={<PackageDetailPage />} />
              <Route path="/package-booking" element={<PackageBookingPage />} />
              <Route path="/booking-success" element={<BookingSuccessPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/profile" element={<ProtectedRoute><CustomerLayout><ProfilePage /></CustomerLayout></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><CustomerLayout><OrdersPage /></CustomerLayout></ProtectedRoute>} />
              <Route path="/payment/success" element={<CustomerLayout><PaymentSuccess /></CustomerLayout>} />
              <Route path="/payment-success" element={<CustomerLayout><PaymentSuccess /></CustomerLayout>} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Routes>
            <Toaster position="top-center" richColors closeButton />
          </Router>
        </VendorProvider>
      </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
